import { API_BASE, getApiHeaders } from './apiClient';

const API = API_BASE;

export interface RosterPerson {
  id: number;
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
}

export interface RosterEntry {
  cimtRole: string;
  primaryId: number | null;
  alternateId: number | null;
  /** Second alternate (deeper bench). If both primary and the first
   *  alternate are unavailable, this person is the IC of record and
   *  inherits IAP sign-off authority. */
  secondaryAlternateId: number | null;
  primary: RosterPerson | null;
  alternate: RosterPerson | null;
  secondaryAlternate: RosterPerson | null;
  /** True when the role was added by an admin via the custom-roles UI
   *  (i.e. not one of the base 14). Drives whether we render the row's
   *  delete affordance. */
  isCustom?: boolean;
}

export interface CimtRoleEntry {
  name: string;
  isCustom: boolean;
}

async function apiFetch(url: string, opts?: RequestInit) {
  const res = await fetch(url, {
    ...opts,
    headers: { ...getApiHeaders(), ...(opts?.headers ?? {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export async function getRoster(weekStart: string): Promise<{ weekStart: string; roster: RosterEntry[] }> {
  return apiFetch(`${API}/cima/roster?week=${weekStart}`);
}

export async function assignRole(
  weekStart: string,
  cimtRole: string,
  primaryId: number | null,
  alternateId: number | null,
  secondaryAlternateId: number | null = null,
): Promise<void> {
  await apiFetch(`${API}/cima/roster`, {
    method: 'PUT',
    body: JSON.stringify({ weekStart, cimtRole, primaryId, alternateId, secondaryAlternateId }),
  });
}

/** Bulk-copy a week's roster assignments to another week. Used by the
 *  "Copy previous week" button on the next-week roster view so admins
 *  don't have to re-enter every role. Returns the count of rows copied
 *  (one per CIMT role that was assigned in fromWeek). */
export async function copyRoster(fromWeek: string, toWeek: string): Promise<{ copied: number; fromWeek: string; toWeek: string }> {
  return apiFetch(`${API}/cima/roster/copy`, {
    method: 'POST',
    body: JSON.stringify({ fromWeek, toWeek }),
  });
}

/** List the full set of CIMT roles for the current client — base 14 plus
 *  any custom roles the admin has added, MINUS any base roles the admin
 *  has hidden via the roster delete affordance. Also returns the names of
 *  hidden base roles so the UI can offer Restore chips. */
export async function listCimtRoles(): Promise<{ roles: CimtRoleEntry[]; disabled: string[] }> {
  return apiFetch(`${API}/cima/roster/roles`);
}

/** Add a custom CIMT role (e.g. "Onboarding Lead", "Site Safety Officer").
 *  Idempotent — re-adding an existing custom role is a no-op. Refuses to
 *  shadow a base CIMT role. */
export async function addCimtRole(roleName: string): Promise<{ added: boolean; roleName: string }> {
  return apiFetch(`${API}/cima/roster/roles`, {
    method: 'POST',
    body: JSON.stringify({ roleName }),
  });
}

/** Remove a custom CIMT role. Cascades: drops roster assignments for that
 *  role across all weeks and clears it from any cima_staff.cimt_role refs.
 *  Refuses to delete a base CIMT role. */
export async function deleteCimtRole(roleName: string): Promise<{ deleted: boolean; affected: number }> {
  return apiFetch(`${API}/cima/roster/roles/${encodeURIComponent(roleName)}`, {
    method: 'DELETE',
  });
}

/** Manually trigger the weekly roster reminder Lambda (admin/manager only).
 *  Useful for testing or sending an immediate ad-hoc reminder.
 *  Returns a summary of the run (emailsSent, sentTo, etc.). */
export async function sendRosterReminder(): Promise<{
  triggered: boolean;
  result: {
    ok: boolean;
    weekStart?: string;
    adminsFound?: number;
    emailsSent?: number;
    emailsFailed?: number;
    sentTo?: string[];
    testMode?: boolean;
    testRecipient?: string | null;
  };
}> {
  return apiFetch(`${API}/cima/roster/send-reminder`, { method: 'POST' });
}
